import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import { cn } from '@/lib/utils';
import type { TripListViewState } from '@/components/trips/table/types';
import type { Translator } from '@/i18n/useTranslations';

interface TripListPaginationProps {
  t: Translator;
  viewState: TripListViewState;
  className?: string;
  maxButtons?: number;
}

export function TripListPagination({
  t,
  viewState,
  className,
  maxButtons,
}: TripListPaginationProps) {
  const {
    totalRecords,
    startRecord,
    endRecord,
    currentPage,
    totalPages,
    pageNumbers,
    handlePageChange,
    showSpinner,
  } = viewState;

  if (totalRecords <= 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-3 border-t border-[#f0f0f0] px-6 py-4 md:flex-row md:items-center md:justify-between',
        className
      )}
    >
      <div className="text-sm leading-5 text-[#666666]">
        {t('pagination.range', { from: startRecord, to: endRecord, total: totalRecords })}
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="white"
          size="none"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="flex h-9 items-center gap-2 rounded-full px-3 text-sm font-medium disabled:cursor-not-allowed"
        >
          <svg
            aria-hidden="true"
            className="h-4 w-4"
            viewBox="0 0 25 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14.5 7L9.5 12L14.5 17"
              stroke={currentPage <= 1 ? '#C5C5C5' : '#292929'}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>{t('pagination.previous')}</span>
        </Button>

        {buildPageNumbers(pageNumbers, currentPage, totalPages, maxButtons).map((pageNumber) => {
          const isActive = pageNumber === currentPage;
          return (
            <Button
              key={pageNumber}
              variant="white"
              size="none"
              onClick={() => handlePageChange(pageNumber)}
              className={`h-9 min-w-[2.25rem] rounded-full px-3 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-[#242424] text-white hover:bg-[#242424]'
                  : 'bg-white text-[#292929] hover:bg-[#f5f5f5]'
              }`}
            >
              {pageNumber}
            </Button>
          );
        })}

        <Button
          variant="white"
          size="none"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="flex h-9 items-center gap-2 rounded-full px-3 text-sm font-medium disabled:cursor-not-allowed"
        >
          <span>{t('pagination.next')}</span>
          <svg
            aria-hidden="true"
            className="h-4 w-4"
            viewBox="0 0 25 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.5 7L15.5 12L10.5 17"
              stroke={currentPage >= totalPages ? '#C5C5C5' : '#292929'}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Button>

        {showSpinner && <Spinner variant="circle" className="h-4 w-4 text-[#242424]" />}
      </div>
    </div>
  );
}

function buildPageNumbers(
  fallback: number[],
  currentPage: number,
  totalPages: number,
  maxButtons?: number
) {
  if (!maxButtons || totalPages <= maxButtons) {
    return fallback;
  }

  const half = Math.floor(maxButtons / 2);
  let start = Math.max(1, currentPage - half);
  const end = Math.min(totalPages, start + maxButtons - 1);

  if (end - start + 1 < maxButtons) {
    start = Math.max(1, end - maxButtons + 1);
  }

  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}
